import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EquipmentService } from "@root/services/equipment";
import { useNotification } from "./useNotification";
import { PAGINATION_DEFAULTS } from "@root/constants";

export function useEquipments() {
  const [equipmentsByDepartmentData, setEquipmentsByDepartmentData] =
    useState<any>([]);
  const [equipmentCount, setEquipmentCount] = useState<number>(0);
  const [equipmentHasMore, setEquipmentHasMore] = useState<boolean>(true);
  const [
    addEquipmentsByDepartmentLoading,
    setAddEquipmentsByDepartmentLoading,
  ] = useState(false);
  const [
    updateEquipmentsByDepartmentLoading,
    setUpdateEquipmentsByDepartmentLoading,
  ] = useState(false);
  const [
    deleteEquipmentsByDepartmentLoading,
    setDeleteEquipmentsByDepartmentLoading,
  ] = useState(false);
  const [
    getEquipmentsByDepartmentLoading,
    setGetEquipmentsByDepartmentLoading,
  ] = useState(false);

  const equipmentService = new EquipmentService();
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");

  const fetchEquipmentsByDepartment = async (payload: {
    id: number;
    searchQuery?: string;
    offset?: number;
    limit?: number;
    append?: boolean;
  }) => {
    const {
      id,
      searchQuery = "",
      offset = 0,
      limit = PAGINATION_DEFAULTS.PAGESIZE,
      append = false,
    } = payload;

    setGetEquipmentsByDepartmentLoading(true);
    try {
      const response = await equipmentService.fetchEquipmentsByDepartment(id, {
        offset,
        limit,
        searchQuery,
      });

      if (response?.data?.data) {
        const newRows = response.data.data.rows || [];
        const totalCount = response.data.data.count || 0;

        setEquipmentCount(totalCount);

        if (append) {
          // Deduplicate data by id
          setEquipmentsByDepartmentData((prev: any[]) => {
            const existingIds = new Set(prev.map((item: any) => item.id));
            const uniqueNewRows = newRows.filter(
              (item: any) => !existingIds.has(item.id)
            );
            return [...prev, ...uniqueNewRows];
          });
        } else {
          setEquipmentsByDepartmentData(newRows);
        }

        // Check if there's more data to load
        setEquipmentHasMore(offset + newRows.length < totalCount);

        return response?.data;
      }
    } catch (error: any) {
      showNotification({
        message: t("assessments.notifications.fetchEquipmentsError"),
        type: "error",
      });
      throw error;
    } finally {
      setGetEquipmentsByDepartmentLoading(false);
    }
  };

  return {
    equipmentsByDepartmentData,
    setEquipmentsByDepartmentData,
    equipmentCount,
    equipmentHasMore,
    getEquipmentsByDepartmentLoading,
    fetchEquipmentsByDepartment,
  };
}
